import React, { useState, useRef, createRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import { AuthShell, authStyles as styles } from 'components/Auth';
import { useDispatch, useSelector } from 'react-redux';
import { extractEmailFromLocation } from './utils';
import { sendAuthCode, submitAuthCode, update } from 'redux/modules/auth';
import { trackEvent } from 'helpers/Analytics';
import { buildVisitorSegmentation } from 'helpers/signupAttributionUtm';
import {
  getBuyerLoginPath,
  getSellerLoginPath,
} from 'helpers/clientOnboarding';
import { maybeTrackAudienceTransitionOnSignup } from 'helpers/audienceTracking';
import { setLastAuth, LAST_AUTH_METHODS } from 'helpers/lastAuth';

const DIGIT_RE = /^[0-9]$/;

const AuthCode = ({ location }) => {
  const dispatch = useDispatch();
  const email = extractEmailFromLocation(location);

  // Extract referer, sendEmail, and tw_user_resolved from URL search params
  const { referer, sendEmail, twUserResolved } = (() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      return {
        referer: params.get('referer') || '',
        sendEmail: params.get('sendEmail') === 'true',
        twUserResolved: params.get('tw_user_resolved') === 'true',
      };
    }
    return {
      referer: location.query.referer || '',
      sendEmail: location.query.sendEmail === 'true',
      twUserResolved: location.query.tw_user_resolved === 'true',
    };
  })();

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isCheckingCooldown, setIsCheckingCooldown] = useState(true);
  const inputRefs = useRef(
    Array(6)
      .fill(null)
      .map(() => createRef())
  );
  const isSubmittingRef = useRef(false);
  const autoSubmitArmedRef = useRef(true);

  const authSelect = useSelector((state) => state.auth);

  const {
    verifyingAuthCode,
    verifiedAuthCode,
    verifyAuthCodeResponse,
    verifyAuthCodeError,
  } = authSelect;

  useEffect(() => {
    if (twUserResolved) {
      trackEvent('post-posted-brief-success');
    }
  }, [twUserResolved]);

  useEffect(() => {
    if (verifiedAuthCode && !verifyAuthCodeError && !error) {
      setSuccess(true);
      // Persist before any redirect effect can navigate away.
      setLastAuth({ method: LAST_AUTH_METHODS.CODE, email });
      // Track successful code verification
      trackEvent('auth-code-verification-success', { email, referer });
      if (verifyAuthCodeResponse && referer !== 'signin') {
        maybeTrackAudienceTransitionOnSignup({ user: verifyAuthCodeResponse });
      }
    }
  }, [
    verifiedAuthCode,
    verifyAuthCodeError,
    error,
    verifyAuthCodeResponse,
    referer,
    email,
  ]);

  useEffect(() => {
    if (!verifyAuthCodeResponse || !success) return;

    const redirectPath = getRedirectPath();
    // Track redirect after successful verification
    trackEvent('auth-code-redirect', {
      email,
      referer,
      userType: verifyAuthCodeResponse.type || 'unknown',
      redirectPath,
    });

    let cancelled = false;

    const doRedirect = async () => {
      if (cancelled) return;
      if (twUserResolved) {
        if (verifyAuthCodeResponse?.jobs_posted_count <= 1) {
          trackEvent('post-signed-up');
        }

        // ICP / visitor segmentation is client-only (TP-240)
        const visitor = buildVisitorSegmentation();
        if (
          visitor &&
          verifyAuthCodeResponse?.id &&
          verifyAuthCodeResponse?.type === 'buyer'
        ) {
          try {
            await Promise.resolve(
              dispatch(update(verifyAuthCodeResponse, { visitor }))
            );
          } catch (_) {
            // Ignore update errors so the redirect still proceeds
          }
        }
      }

      if (!cancelled) {
        // Re-write immediately before navigation (localStorage is sync).
        setLastAuth({ method: LAST_AUTH_METHODS.CODE, email });
        window.location.href = redirectPath;
      }
    };

    let timerId;
    if (twUserResolved) {
      doRedirect();
    } else {
      timerId = setTimeout(doRedirect, 800);
    }

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [verifyAuthCodeResponse, success, referer, email, twUserResolved]);

  useEffect(() => {
    if (verifyAuthCodeError) {
      isSubmittingRef.current = false;
      autoSubmitArmedRef.current = true;

      // Track verification error
      trackEvent('auth-code-verification-error', {
        email,
        referer,
        error: verifyAuthCodeError,
      });

      // If the code has expired, automatically send a new one
      if (
        verifyAuthCodeError === 'Verification code expired' ||
        verifyAuthCodeError === 'No verification code found'
      ) {
        // Clear input fields
        setCode(['', '', '', '', '', '']);

        // Focus the first input field
        if (inputRefs.current[0].current) {
          inputRefs.current[0].current.focus();
        }

        // Send a new code
        dispatch(sendAuthCode(email));

        // Set a custom error message
        setError('A new verification code has been sent to your email');

        // Set resend cooldown
        localStorage.setItem('authCodeLastResend', Date.now().toString());
        setResendCooldown(60);
        setIsCheckingCooldown(false);
      } else {
        // For other errors, just set the error message
        setError(verifyAuthCodeError);
      }
    }
  }, [verifyAuthCodeError, dispatch, email, inputRefs]);

  // Track page view and focus the first input field when the component mounts
  useEffect(() => {
    // Track page view when component mounts
    trackEvent('user-lands-on-auth-code', { email, referer });

    if (inputRefs.current[0].current) {
      inputRefs.current[0].current.focus();
    }

    if (sendEmail) {
      dispatch(sendAuthCode(email));
    }
  }, []);

  useEffect(() => {
    // Check if there's an existing cooldown
    const lastResendTime = localStorage.getItem('authCodeLastResend');
    let initialCooldown = 0;

    if (lastResendTime) {
      // If there's a stored time, calculate remaining cooldown
      initialCooldown = Math.max(
        0,
        60 - Math.floor((Date.now() - parseInt(lastResendTime)) / 1000)
      );

      if (initialCooldown <= 0) {
        localStorage.removeItem('authCodeLastResend');
      }
    } else {
      // If no stored time, set initial cooldown to 60 seconds when page loads
      initialCooldown = 60;
      localStorage.setItem('authCodeLastResend', Date.now().toString());
    }

    if (initialCooldown > 0) {
      setResendCooldown(initialCooldown);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            clearInterval(timer);
            localStorage.removeItem('authCodeLastResend');
            setIsCheckingCooldown(false);
          }
          return Math.max(0, newValue);
        });
      }, 1000);
      return () => clearInterval(timer);
    }

    // Set checking to false after we've determined the cooldown state
    setIsCheckingCooldown(false);
  }, []);

  const submitCode = (codeArray, submissionType) => {
    const joined = codeArray.join('');
    if (joined.length !== 6) {
      setError('Please enter a valid 6-digit code');
      trackEvent('auth-code-invalid-submission', {
        email,
        referer,
        codeLength: joined.length,
      });
      return;
    }

    if (success || isSubmittingRef.current || verifyingAuthCode) {
      return;
    }

    isSubmittingRef.current = true;
    autoSubmitArmedRef.current = false;

    trackEvent('auth-code-submission-attempt', {
      email,
      referer,
      submissionType,
    });

    dispatch(submitAuthCode(joined, email));
  };

  const maybeAutoSubmit = (nextCode) => {
    if (
      nextCode.every((d) => DIGIT_RE.test(d)) &&
      autoSubmitArmedRef.current &&
      !isSubmittingRef.current &&
      !success &&
      !verifyingAuthCode
    ) {
      submitCode(nextCode, 'auto');
    }
  };

  const applyDigits = (rawDigits, startIndex = 0) => {
    const digits = String(rawDigits)
      .replace(/\D/g, '')
      .slice(0, 6 - startIndex)
      .split('');
    if (!digits.length) {
      return;
    }

    const newCode = [...code];
    digits.forEach((char, i) => {
      const idx = startIndex + i;
      if (idx < 6) {
        newCode[idx] = char;
      }
    });
    setCode(newCode);

    const lastFilled = Math.min(startIndex + digits.length - 1, 5);
    if (inputRefs.current[lastFilled]?.current) {
      inputRefs.current[lastFilled].current.focus();
    }

    maybeAutoSubmit(newCode);
  };

  const handleChange = (index, value) => {
    setError('');
    setResendSuccess(false);

    if (value.length > 1) {
      applyDigits(value, 0);
      return;
    }

    const digit = value.replace(/\D/g, '').slice(0, 1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);

    if (digit !== '' && index < 5) {
      inputRefs.current[index + 1].current.focus();
    }

    maybeAutoSubmit(newCode);
  };

  const handleKeyDown = (index, e) => {
    setError('');
    if (DIGIT_RE.test(e.key)) {
      e.preventDefault();
      const newCode = [...code];
      newCode[index] = e.key;
      setCode(newCode);

      if (index < 5) {
        inputRefs.current[index + 1].current.focus();
      }

      maybeAutoSubmit(newCode);
    } else if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
        inputRefs.current[index - 1].current.focus();
        autoSubmitArmedRef.current = true;
      } else {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
        autoSubmitArmedRef.current = true;
      }
    } else if (e.key === 'ArrowLeft') {
      if (index > 0) {
        inputRefs.current[index - 1].current.focus();
      }
    } else if (e.key === 'ArrowRight') {
      if (index < 5) {
        inputRefs.current[index + 1].current.focus();
      }
    }
  };

  const handlePaste = (e) => {
    setError('');
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    applyDigits(pastedData, 0);
  };

  const getRedirectPath = () => {
    const userType = verifyAuthCodeResponse?.type;

    if (referer === 'signin') {
      return userType === 'seller'
        ? getSellerLoginPath()
        : userType === 'buyer'
        ? getBuyerLoginPath()
        : '/';
    }

    if (referer) {
      // For signin or protected routes, go to the home page but include the referer as a query param
      // This allows routes.js to pick up the referer and handle the redirect
      return `/redirecting?originalReferer=${encodeURIComponent(referer)}`;
    }

    return userType === 'buyer' ? '/onboarding/client' : '/onboarding';
  };

  const errorMessage = useMemo(() => {
    switch (error) {
      case 'Verification code does not match':
        return "This code does not match the one we sent to your email. Make sure you've used the most recent code and try again";
      case 'Verification code expired':
        return 'This code has expired, please request a new one using the "Resend Code" link below';
      case 'No verification code found':
        return 'No verification code found, please request a new one using the "Resend Code" link below';
      case 'User not found':
        return 'No user found with this email address. If signing in make sure your email was correct, otherwise contact support';
      case 'Please enter a valid 6-digit code':
        return error;
      case 'A new verification code has been sent to your email':
        return "Your previous code had expired. We've automatically sent a new verification code to your email.";
      default:
        return 'Something went wrong, please contact support';
    }
  }, [error]);

  const handleSubmit = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitCode(code, 'manual');
  };

  const handleResendAuthCode = () => {
    if (resendCooldown > 0) return;

    // Track manual code resend request
    trackEvent('auth-code-manual-resend', {
      email,
      referer,
    });

    // Clear error message
    setError('');

    // Clear input fields
    setCode(['', '', '', '', '', '']);
    isSubmittingRef.current = false;
    autoSubmitArmedRef.current = true;

    // Show success message
    setResendSuccess(true);

    // Focus the first input field
    setTimeout(() => {
      if (inputRefs.current[0].current) {
        inputRefs.current[0].current.focus();
      }
    }, 0);

    dispatch(sendAuthCode(email));
    localStorage.setItem('authCodeLastResend', Date.now().toString());
    setResendCooldown(60);
    setIsCheckingCooldown(false); // Ensure the button shows the countdown

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          clearInterval(timer);
          localStorage.removeItem('authCodeLastResend');
          setIsCheckingCooldown(false);
        }
        return Math.max(0, newValue);
      });
    }, 1000);
  };

  const resendDisabled = resendCooldown > 0 || isCheckingCooldown;

  return (
    <AuthShell>
      <Helmet title="Verify code" />

      <div className={styles.headingBlock}>
        <h1 className={styles.title}>Check your email</h1>
        <p className={styles.subline}>
          We sent a 6-digit code to <strong>{email}</strong>.{' '}
          <a href="/signin">Wrong email?</a>
        </p>
      </div>

      <form className={styles.formStack} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label className={styles.label} id="enter-code-label">
            Enter code
          </label>
          <div
            className={styles.codeRow}
            role="group"
            aria-labelledby="enter-code-label"
          >
            {code.map((digit, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onClick={(e) => e.target.select()}
                onFocus={(e) => e.target.select()}
                ref={inputRefs.current[index]}
                className={styles.codeInput}
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${index + 1} of 6`}
              />
            ))}
          </div>
        </div>

        <p className={styles.smallPrint}>
          Paste works too. The code is valid for 15 minutes.
        </p>

        <div aria-live="polite">
          {error ? (
            <p className={styles.errorMessage} role="alert">
              {errorMessage}
            </p>
          ) : null}
          {resendSuccess && !error ? (
            <p className={styles.successMessage}>
              A new verification code has been sent to your email
            </p>
          ) : null}
        </div>

        <ButtonWithLoader
          loading={verifyingAuthCode}
          disabled={verifyingAuthCode}
          onClick={handleSubmit}
          bsStyle="primary"
          className={styles.primaryButton}
          text="Verify and sign in"
          success={success}
        />

        <p className={styles.microcopy}>
          Didn&apos;t get it?{' '}
          <button
            type="button"
            className={styles.labelRowLink}
            onClick={handleResendAuthCode}
            disabled={resendDisabled}
          >
            {resendCooldown > 0
              ? `Resend code in ${resendCooldown}s`
              : 'Resend code'}
          </button>{' '}
          — and check your spam folder. Adding{' '}
          <strong>hello@twinehq.com</strong> to safe senders helps next time.
        </p>
      </form>
    </AuthShell>
  );
};

AuthCode.propTypes = {
  location: PropTypes.object,
};

export default AuthCode;
